<div class="dataset-tab flex flex-col gap-6">
  <div class="dataset-tab__columns flex flex-col gap-6 lg:flex-row lg:items-start">
    <div class="dataset-tab__col-main flex flex-col gap-6 flex-1 min-w-0 lg:basis-[66%]">
      <section class="dataset-tab__card">
        <h2 class="dataset-tab__card-title">Description</h2>
        @if (datasetDescription()) {
          <p class="dataset-tab__body text-[var(--text-color-2)] text-sm leading-relaxed whitespace-pre-wrap m-0">
            {{ datasetDescription() }}
          </p>
        } @else {
          <p class="dataset-tab__body text-[var(--text-muted-on-light)] m-0">No description available.</p>
        }
      </section>

      <section class="dataset-tab__card">
        <h2 class="dataset-tab__card-title">Dataset details</h2>
        <div class="dataset-tab__details-grid grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">Dataset title</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['datasetTitle'] || '—' }}</span>
          </div>
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">Dataset Permission</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['datasetPermission'] || '—' }}</span>
          </div>
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">Organisation Name</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['organisationName'] || '—' }}</span>
          </div>
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">Organization Type</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['organizationType'] || '—' }}</span>
          </div>
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">Verified by</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['verifiedBy'] || '—' }}</span>
          </div>
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">Created at</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['createdAt'] || '—' }}</span>
          </div>
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">Industry</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['industry'] || '—' }}</span>
          </div>
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">Publisher</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['publisher'] || '—' }}</span>
          </div>
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">Update frequency</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['updateFrequency'] || '—' }}</span>
          </div>
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">Year Range</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['yearRange'] || '—' }}</span>
          </div>
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">Last updated</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['lastUpdated'] || '—' }}</span>
          </div>
          <div class="dataset-tab__cell">
            <span class="dataset-tab__cell-label">License</span>
            <span class="dataset-tab__cell-value">{{ detailRows()['license'] || '—' }}</span>
          </div>
        </div>
      </section>

      @if (resourceServersList().length > 0) {
        <section class="dataset-tab__card dataset-tab__resource-servers">
          <h2 class="dataset-tab__card-title dataset-tab__resource-servers-title">
            Resource Server Details
          </h2>
          <div class="dataset-tab__resource-server-table-wrap">
            <table class="dataset-tab__resource-server-table w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th scope="col" class="dataset-tab__rs-th dataset-tab__rs-th--name text-left">Name</th>
                  <th scope="col" class="dataset-tab__rs-th dataset-tab__rs-th--url text-left">URL</th>
                  <th scope="col" class="dataset-tab__rs-th dataset-tab__rs-th--types text-right">Query Types</th>
                </tr>
              </thead>
              <tbody>
                @for (rs of resourceServersList(); track $index) {
                  <tr class="dataset-tab__rs-row">
                    <td class="dataset-tab__rs-td dataset-tab__rs-name">{{ resourceServerDisplayName(rs) }}</td>
                    <td class="dataset-tab__rs-td dataset-tab__rs-url">{{ rs.url || '—' }}</td>
                    <td class="dataset-tab__rs-td dataset-tab__rs-types text-right">
                      @if (resourceServerQueryTypeLabels(rs).length) {
                        <span class="dataset-tab__rs-types-inner inline-flex flex-wrap justify-end gap-2">
                          @for (qt of resourceServerQueryTypeLabels(rs); track qt) {
                            <span class="dataset-tab__rs-query-pill">{{ qt }}</span>
                          }
                        </span>
                      } @else {
                        <span class="dataset-tab__rs-empty">—</span>
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      }
    </div>

    <aside class="dataset-tab__col-side flex flex-col gap-6 flex-1 min-w-0 lg:basis-[33%] lg:max-w-md">
      <section class="dataset-tab__card dataset-tab__card--sidebar">
        <h2 class="dataset-tab__card-title">Activity Overview</h2>
        <div class="dataset-tab__badges flex flex-wrap gap-3">
          <div class="dataset-tab__badge inline-flex items-center gap-2">
            <img src="/icons/thumbs-up-teal.svg" width="18" height="18" alt="" />
            <span>{{ formatMetric(activityMetrics().likes) }}</span>
          </div>
          <div class="dataset-tab__badge inline-flex items-center gap-2">
            <img src="/icons/eye-teal.svg" width="18" height="18" alt="" />
            <span>{{ formatMetric(activityMetrics().views) }}</span>
          </div>
          <div class="dataset-tab__badge inline-flex items-center gap-2">
            <img src="/icons/download-teal.svg" width="18" height="18" alt="" />
            <span>{{ formatMetric(activityMetrics().downloads) }}</span>
          </div>
        </div>
      </section>

      <section class="dataset-tab__card dataset-tab__card--sidebar">
        <h2 class="dataset-tab__card-title">Metadata</h2>
        <div class="dataset-tab__meta-stack flex flex-col gap-6">
          <div>
            <span class="dataset-tab__meta-label">Uploaded by</span>
            <p class="dataset-tab__meta-value m-0 mt-1">{{ detailRows()['organisationName'] || '—' }}</p>
          </div>
          <div>
            <span class="dataset-tab__meta-label">Formats</span>
            <div class="dataset-tab__tags flex flex-wrap gap-2 mt-2">
              @for (fmt of parseFormats(detailRows()['fileFormat'] || ''); track fmt) {
                <span class="dataset-tab__tag">{{ fmt }}</span>
              }
              @if (!parseFormats(detailRows()['fileFormat'] || '').length) {
                <span class="dataset-tab__tag">—</span>
              }
            </div>
          </div>
          <div>
            <span class="dataset-tab__meta-label">Keywords</span>
            <div class="dataset-tab__tags flex flex-wrap gap-2 mt-2">
              @for (kw of datasetTags(); track kw) {
                <span class="dataset-tab__tag">{{ kw }}</span>
              }
              @if (!datasetTags().length) {
                <span class="dataset-tab__meta-muted">No keywords</span>
              }
            </div>
          </div>
        </div>
      </section>

      <section class="dataset-tab__card dataset-tab__card--sidebar dataset-tab__card--actions">
        <h2 class="dataset-tab__card-title">Actions</h2>
        <div
          class="dataset-tab__actions-grid"
          [class.dataset-tab__actions-grid--restricted]="isRestrictedDataset()"
          role="group"
          aria-label="Dataset actions"
        >
          @if (hasDataSample()) {
            <button type="button" class="dataset-tab__btn dataset-tab__btn--secondary" (click)="openSampleData()">
              <span class="dataset-tab__btn-icon" aria-hidden="true">
                <img src="/icons/dataset-black.svg" width="20" height="20" alt="" />
              </span>
              <span class="dataset-tab__btn-label">Sample Data</span>
            </button>
          }
          <button type="button" class="dataset-tab__btn dataset-tab__btn--secondary" (click)="openViewCode()">
            <span class="dataset-tab__btn-icon" aria-hidden="true">
              <img
                class="dataset-tab__btn-icon-img"
                src="/icons/code-brackets.svg"
                width="20"
                height="20"
                alt=""
              />
            </span>
            <span class="dataset-tab__btn-label">View Code</span>
          </button>
          @if (canShowQueryData) {
            <button type="button" class="dataset-tab__btn dataset-tab__btn--secondary" (click)="onQueryData()">
              <span class="dataset-tab__btn-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <ellipse cx="12" cy="5" rx="9" ry="3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <span class="dataset-tab__btn-label">Query Data</span>
            </button>
          }
          <button
            type="button"
            class="dataset-tab__btn dataset-tab__btn--primary"
            [disabled]="getDataDisabled()"
            [attr.title]="getDataDisabledTitle() || null"
            (click)="onGetData()"
          >
            <span class="dataset-tab__btn-icon" aria-hidden="true">
              <img src="/icons/arrow-white-circle.svg" width="20" height="20" alt="" />
            </span>
            <span class="dataset-tab__btn-label">Get Data</span>
          </button>
          @if (showRequestAccessButton()) {
            <button type="button" class="dataset-tab__btn dataset-tab__btn--secondary" (click)="onRequestAccess()">
              <span class="dataset-tab__btn-icon" aria-hidden="true">
                <img src="/icons/request-access.svg" width="20" height="20" alt="" />
              </span>
              <span class="dataset-tab__btn-label">Request Access</span>
            </button>
          }
          @if (showDownloadButton()) {
            <button
              type="button"
              class="dataset-tab__btn dataset-tab__btn--primary"
              [class.dataset-tab__btn--span-cols]="showRequestAccessButton() && showDownloadButton()"
              (click)="onDownload()"
            >
              <span class="dataset-tab__btn-icon" aria-hidden="true">
                <img src="/icons/download-white.svg" width="20" height="20" alt="" />
              </span>
              <span class="dataset-tab__btn-label">Download</span>
            </button>
          }
        </div>
      </section>
    </aside>
  </div>
</div>

<app-modal
  [(visible)]="showSampleDataModal"
  header="Sample Data"
  [width]="'min(92vw, 720px)'"
  [showFooter]="false"
  styleClass="custom-modal dataset-detail-dataset-tab-modal"
>
  <div class="dataset-tab__modal-code-block">
    <div class="dataset-tab__modal-toolbar">
      <span class="dataset-tab__modal-toolbar-meta">{{ sampleDataJson().split('\n').length }} lines</span>
      <button
        type="button"
        class="dataset-tab__modal-icon-btn"
        (click)="copySampleData()"
        title="Copy to clipboard"
        aria-label="Copy sample data"
      >
        <img src="/icons/copy-clipboard.svg" width="18" height="18" alt="" />
      </button>
    </div>
    <pre class="dataset-tab__modal-pre dataset-tab__modal-pre--flush">{{ sampleDataJson() }}</pre>
  </div>
</app-modal>

<app-modal
  [(visible)]="showViewCodeModal"
  header="View Code"
  [width]="'min(92vw, 720px)'"
  [showFooter]="false"
  styleClass="custom-modal dataset-detail-dataset-tab-modal"
>
  <div class="dataset-tab__modal-code-block">
    <div class="dataset-tab__modal-toolbar">
      <span class="dataset-tab__modal-toolbar-meta"
        >{{ viewCodeText() ? viewCodeText().split('\n').length : 0 }} lines</span
      >
      <div class="dataset-tab__modal-toolbar-actions">
        <button
          type="button"
          class="dataset-tab__modal-icon-btn"
          (click)="copyCode()"
          title="Copy code"
          aria-label="Copy code"
        >
          <img src="/icons/copy-clipboard.svg" width="18" height="18" alt="" />
        </button>
        <button
          type="button"
          class="dataset-tab__modal-icon-btn"
          (click)="downloadCode()"
          title="Download code"
          aria-label="Download code"
        >
          <img src="/icons/download-teal.svg" width="18" height="18" alt="" />
        </button>
      </div>
    </div>
    <pre class="dataset-tab__modal-pre dataset-tab__modal-pre--code dataset-tab__modal-pre--flush">{{ viewCodeText() }}</pre>
  </div>
</app-modal>

<app-modal
  [visible]="showGetDataModal"
  header="Get Data"
  [width]="'min(92vw, 90vh)'"
  [showFooter]="false"
  styleClass="custom-modal dataset-detail-dataset-tab-modal"
  (visibleChange)="onGetDataModalToggle($event)"
>
  <div class="dataset-tab__get-data">
    @if (getDataLoading) {
      <div class="dataset-tab__get-data-loading flex flex-col items-center gap-3 py-10">
        <span class="dataset-tab__get-data-spinner" aria-hidden="true"></span>
        <p class="m-0 text-sm text-[var(--text-secondary-on-light)]">Loading data…</p>
      </div>
    } @else if (getDataPdfSafeUrl) {
      <iframe
        class="dataset-tab__get-data-iframe w-full border-0 rounded-md"
        [src]="getDataPdfSafeUrl"
        title="File preview"
      ></iframe>
    } @else if (getDataFileDownloadUrl) {
      <p class="dataset-tab__modal-lead text-[var(--text-secondary-on-light)] text-sm m-0 mb-3">
        Preview is not available for this file type. Download to open it locally.
      </p>
      <a
        class="dataset-tab__get-data-download-link inline-flex items-center gap-2 font-semibold text-sm"
        [href]="getDataFileDownloadUrl"
        [attr.download]="getDataFileName"
        target="_blank"
        rel="noopener noreferrer"
      >
        <img src="/icons/download-teal.svg" width="18" height="18" alt="" />
        Download {{ getDataFileName || 'file' }}
      </a>
    } @else {
      <p class="dataset-tab__modal-lead text-[var(--text-secondary-on-light)] text-sm m-0 mb-3">
        Use the resource server endpoint below with your platform credentials.
      </p>
      <pre class="dataset-tab__modal-pre">{{ getDataSnippet() }}</pre>
    }
  </div>
</app-modal>

<app-modal
  [(visible)]="showRequestAccessModal"
  header="Additional information for download request"
  [width]="'min(92vw, 700px)'"
  [showFooter]="false"
  styleClass="custom-modal dataset-detail-dataset-tab-modal"
>
  <div class="dataset-tab__request-access">
    @if (!userHasOrganisation) {
      <div class="dataset-tab__request-access-field">
        <label class="dataset-tab__request-access-label">Request as</label>
        <div class="dataset-tab__request-access-radio-cards">
          <label
            class="dataset-tab__request-access-card"
            [class.dataset-tab__request-access-card--selected]="requestForm.requestAs === 'Individual'"
          >
            <input
              type="radio"
              name="requestAsDataset"
              value="Individual"
              [(ngModel)]="requestForm.requestAs"
              class="dataset-tab__request-access-radio"
            />
            <span class="dataset-tab__request-access-card-text">Individual</span>
          </label>
          <label
            class="dataset-tab__request-access-card"
            [class.dataset-tab__request-access-card--selected]="requestForm.requestAs === 'Organisation'"
          >
            <input
              type="radio"
              name="requestAsDataset"
              value="Organisation"
              [(ngModel)]="requestForm.requestAs"
              class="dataset-tab__request-access-radio"
            />
            <span class="dataset-tab__request-access-card-text">Organisation</span>
          </label>
        </div>
      </div>
      @if (requestForm.requestAs === 'Organisation') {
        <div class="dataset-tab__request-access-join">
          <p class="m-0 text-sm text-[var(--text-secondary-on-light)]">
            You need to join or create an organisation to request access as an organisation.
          </p>
          <button type="button" class="dataset-tab__request-access-link-btn" (click)="goToJoinOrganisation()">
            Join or create an organisation
          </button>
        </div>
      }
      @if (requestForm.requestAs === 'Individual') {
        <div class="dataset-tab__request-access-field">
          <label class="dataset-tab__request-access-label">
            Purpose <span class="dataset-tab__request-access-required">*</span>
          </label>
          <textarea
            class="dataset-tab__request-access-textarea"
            [(ngModel)]="requestForm.purposeDescription"
            rows="4"
            maxlength="200"
            placeholder="Please briefly describe how you plan to use this dataset"
          ></textarea>
          <span class="dataset-tab__request-access-count">{{ purposeDescriptionLength }}/200 character limit</span>
        </div>
        <div class="dataset-tab__request-access-checkbox-row">
          <input
            type="checkbox"
            id="dataset-request-agree"
            [(ngModel)]="requestForm.agreeToTerms"
            class="dataset-tab__request-access-checkbox"
          />
          <label for="dataset-request-agree" class="dataset-tab__request-access-checkbox-label m-0">
            I have read the
            <a
              href="/documents/Data%20Usage%20Agreement.pdf"
              download="Data Usage Agreement.pdf"
              class="dataset-tab__request-access-inline-link"
              (click)="$event.stopPropagation()"
              >Data Usage Agreement</a
            >
            and agree to the terms of service and privacy policy.
          </label>
        </div>
        <div class="dataset-tab__request-access-actions">
          <button type="button" class="dataset-tab__request-access-cancel" (click)="closeRequestAccessModal()">
            Cancel
          </button>
          <button
            type="button"
            class="dataset-tab__request-access-submit"
            [disabled]="!isRequestFormValid || isSubmittingRequest"
            (click)="submitRequestAccess()"
          >
            Request Access
          </button>
        </div>
      }
    } @else {
      <div class="dataset-tab__request-access-field">
        <label class="dataset-tab__request-access-label">Organisation name</label>
        <input
          type="text"
          class="dataset-tab__request-access-input"
          [(ngModel)]="requestForm.organisationName"
          [readonly]="isOrganisationNameDisabled"
          placeholder="Organisation name"
        />
      </div>
      <div class="dataset-tab__request-access-field">
        <label class="dataset-tab__request-access-label">
          Purpose <span class="dataset-tab__request-access-required">*</span>
        </label>
        <textarea
          class="dataset-tab__request-access-textarea"
          [(ngModel)]="requestForm.purposeDescription"
          rows="4"
          maxlength="200"
          placeholder="Please briefly describe how you plan to use this dataset"
        ></textarea>
        <span class="dataset-tab__request-access-count">{{ purposeDescriptionLength }}/200 character limit</span>
      </div>
      <div class="dataset-tab__request-access-checkbox-row">
        <input
          type="checkbox"
          id="dataset-request-agree-org"
          [(ngModel)]="requestForm.agreeToTerms"
          class="dataset-tab__request-access-checkbox"
        />
        <label for="dataset-request-agree-org" class="dataset-tab__request-access-checkbox-label m-0">
          I have read the
          <a
            href="/documents/Data%20Usage%20Agreement.pdf"
            download="Data Usage Agreement.pdf"
            class="dataset-tab__request-access-inline-link"
            (click)="$event.stopPropagation()"
            >Data Usage Agreement</a
          >
          and agree to the terms of service and privacy policy.
        </label>
      </div>
      <div class="dataset-tab__request-access-actions">
        <button type="button" class="dataset-tab__request-access-cancel" (click)="closeRequestAccessModal()">
          Cancel
        </button>
        <button
          type="button"
          class="dataset-tab__request-access-submit"
          [disabled]="!isRequestFormValid || isSubmittingRequest"
          (click)="submitRequestAccess()"
        >
          Request Access
        </button>
      </div>
    }
  </div>
</app-modal>

<app-model-popup
  [isOpen]="isFeatureDataOpen && featureDataMode.type !== 'OGC_FEATURE'"
  [title]="''"
  [config]="{
    closeOnOverlayClick: false,
    showCloseButton: false,
    showHeader: false,
  }"
  (closeModal)="onCloseFeatureData()"
>
  @if (featureDataMode.type === 'GATEWAY' || featureDataMode.type === 'DEFAULT') {
    <app-gateway-data
      [resourceItemData]="gatewayResourceItem"
      [dataModel]="featureDataMode.dataModel ?? 'model1'"
      [accessTypes]="featureDataMode.accessTypes ?? null"
      (closeRequested)="onCloseFeatureData()"
    />
  } @else if (featureDataMode.type === 'OGC_STAC') {
    <app-ogc-stac-data
      [resourceItemData]="stacResourceItem"
      (closeRequested)="onCloseFeatureData()"
    />
  }
</app-model-popup>

@if (isFeatureDataOpen && featureDataMode.type === 'OGC_FEATURE') {
  <app-ogc-feature-data
    [resourceItemData]="featureDataResourceItem"
    (closeRequested)="onCloseFeatureData()"
  />
}

@if (showLoginModal) {
  <app-login-modal [visible]="true" (visibleChange)="onLoginModalDismiss()"></app-login-modal>
}

<app-modal
  [(visible)]="showQueryDataModal"
  header="Query Data"
  [width]="'min(92vw, 800px)'"
  [showFooter]="false"
  styleClass="custom-modal dataset-detail-dataset-tab-modal"
  (visibleChange)="onQueryDataModalVisibleChange($event)"
>
  <div class="query-data-modal">
    <p class="query-data-modal__intro">Select a date range to fetch temporal data from this dataset.</p>

    @if (queryDataLoading) {
      <div class="query-data-modal__loading">
        <span class="query-data-modal__spinner" aria-hidden="true"></span>
        <p class="query-data-modal__loading-text">Loading temporal data…</p>
      </div>
    } @else {
      <div class="query-data-modal__controls">
        <div class="query-data-modal__field">
          <label class="query-data-modal__label" for="query-data-range">Date Range</label>
          <app-date-range-picker
            id="query-data-range"
            [ngModel]="queryDataDateRange"
            (ngModelChange)="onQueryDataDateRangeChange($event)"
            placeholder="Choose Date Range"
            styleClass="query-data-modal__date-picker"
            [attr.aria-describedby]="queryDataDateError ? 'query-data-error' : null"
          ></app-date-range-picker>
          @if (queryDataDateError) {
            <p class="query-data-modal__error" id="query-data-error" role="alert">{{ queryDataDateError }}</p>
          }
        </div>
        <div class="query-data-modal__actions">
          <button type="button" class="query-data-modal__reset-btn" (click)="onQueryDataReset()">
            Reset
          </button>
          <button
            type="button"
            class="query-data-modal__search-btn"
            [disabled]="!isQueryDataFormValid"
            (click)="onQueryDataSearch()"
          >
            Search
          </button>
        </div>
      </div>
    }

    <div class="query-data-modal__response">
      @if (queryDataResponse) {
        <div class="query-data-modal__response-toolbar">
          <span class="query-data-modal__response-label">Response</span>
          <button
            type="button"
            class="dataset-tab__modal-icon-btn"
            (click)="downloadQueryDataJson()"
            title="Download JSON"
            aria-label="Download JSON response"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <polyline points="7 10 12 15 17 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      }
      <pre class="query-data-modal__response-pre">{{ queryDataLoading ? 'Loading temporal data…' : (queryDataResponse ? getQueryDataResponseFormatted() : 'Run a search to view temporal data for this dataset.') }}</pre>
    </div>
  </div>
</app-modal>
